/**
 * Hole end (REDESIGN.md 7.7): one row per player ranked by hole score, the
 * hole winner flagged, a tap on a row opens that player's final grid. One
 * button: Next hole (with the next world's medallion), plus a scorecard icon.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { holeResults, scoreGrid } from '../../../engine';
import type { GameState } from '../../../engine';
import { fmt, worldFor } from '../../brand';
import type { SeatInfo } from '../../game/types';
import { bonusLabel, chipsFor, label, totalOf } from '../../game/viewModel';
import { PlayerMedallion, Tibbs, WorldMedallion } from '../Art';
import type { TibbsPose } from '../Art';
import type { TibbsStatus } from '../../tibbs/useTibbs';
import { TibbsButton, TibbsCaption } from './TibbsButton';
import { FlatCard } from '../Card';
import { AmphoraIcon, Flag, IconChevron, IconScores } from '../Icons';

export function HoleEndSheet({
  state,
  seats,
  me,
  mode,
  isHost,
  onNext,
  onForce,
  onScores,
  onResults,
  tibbs,
}: {
  /** Tibbs's readout for this hole (absent in the tutorial and on the final hole). */
  tibbs?: {
    status: TibbsStatus;
    loadingText?: string;
    onRequest: () => void;
    onStop: () => void;
    onResume: () => void;
    caption: string | null;
    seats: number[];
    pose: TibbsPose | null;
  };
  state: GameState;
  seats: SeatInfo[];
  me: number | null;
  mode: 'solo' | 'online';
  isHost: boolean;
  onNext: () => void;
  onForce?: () => void;
  onScores: () => void;
  onResults: () => void;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const results = holeResults(state);
  const last = results[results.length - 1];
  if (!last) return null;
  const rows = state.players
    .map((p, i) => ({ p, i, score: last.scores[i] ?? null }))
    .filter((r) => r.score !== null)
    .sort((a, b) => (a.score as number) - (b.score as number) || (a.i === me ? -1 : b.i === me ? 1 : a.i - b.i));
  const low = Math.min(...rows.map((r) => r.score as number));
  const gameOver = state.phase === 'gameOver';
  const world = worldFor(last.hole, state.totalHoles);
  const next = worldFor(last.hole + 1, state.totalHoles);
  const playoffNext = state.phase === 'holeOver' && !!state.playoffPlayers && state.playoffPlayers.length > 1;
  const nextWorld = playoffNext ? worldFor(state.totalHoles + 1, state.totalHoles) : next;
  const myReady = me !== null && seats[me]?.ready;
  const othersPending = state.players.some((_, i) => i !== me && !seats[i]?.isBot && seats[i]?.connected && !seats[i]?.ready);
  const myScore = me !== null ? (last.scores[me] ?? null) : null;
  const reaction: TibbsPose | null = tibbs?.pose ?? (myScore === null ? null : myScore === low ? 'celebrate' : myScore >= 15 ? 'retract' : null);
  const title = last.playoff ? `Playoff ${last.hole - state.totalHoles}` : `Hole ${last.hole}`;

  return (
    <div className="holeend">
      <div className="holeend-head">
        <WorldMedallion world={world} size={34} />
        <h2 className="mark holeend-title" id="he-title">
          {title}
        </h2>
        {reaction && <Tibbs key={reaction} pose={reaction} size={64} className="holeend-tibbs" />}
      </div>
      {tibbs && !gameOver && (
        <div className="he-tibbs">
          <TibbsButton kind="readout" status={tibbs.status} {...(tibbs.loadingText ? { loadingText: tibbs.loadingText } : {})} onRequest={tibbs.onRequest} onStop={tibbs.onStop} onResume={tibbs.onResume} />
          <TibbsCaption text={tibbs.caption} />
        </div>
      )}
      <ul className="he-rows">
        {rows.map(({ p, i, score }, rowIdx) => {
          const expanded = open === i;
          const sb = expanded ? scoreGrid(p.grid, { revealAll: true, options: state.options }) : null;
          const chips = sb ? chipsFor(sb, { splitBonus: false }).chips : [];
          return (
            <li key={i} className={`he-row${i === me ? ' me' : ''}${expanded ? ' open' : ''}${tibbs?.seats.includes(i) ? ' tibbs-on' : ''}`} style={{ '--row': rowIdx } as CSSProperties}>
              <button type="button" className="he-line" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : i)}>
                <PlayerMedallion seat={i} size={30} />
                <span className="he-name">{label(seats, me, i)}</span>
                {score === low && (
                  <span className="he-flag" aria-label="Won the hole">
                    <Flag size={18} />
                  </span>
                )}
                <span className="he-hole num">{fmt(score as number)}</span>
                <span className="he-total num" aria-label={`total ${fmt(totalOf(p))}`}>
                  {fmt(totalOf(p))}
                </span>
                <span className="he-chev" aria-hidden="true">
                  <IconChevron open={expanded} />
                </span>
              </button>
              {expanded && sb && (
                <div className="he-grid">
                  {sb.columns.map((c, ci) => {
                    const chip = chips[ci];
                    const paired = c.kind === 'pair' || c.kind === 'minusFivePair';
                    return (
                      <div key={ci} className={`he-col ${c.kind}${chip?.bonus ? ' bonus' : ''}`}>
                        <div className="he-cards">
                          <FlatCard value={p.grid[ci]?.card?.value ?? null} width={38} />
                          <FlatCard value={p.grid[ci + 4]?.card?.value ?? null} width={38} />
                          {paired && <span className="pair-frame" aria-hidden="true" />}
                        </div>
                        <span className={`he-chip ${c.kind}${chip?.bonus ? ' bonus' : ''}`}>{chip?.label || fmt(c.score)}</span>
                      </div>
                    );
                  })}
                  {sb.bonuses.length > 0 && (
                    <div className="he-bonuses">
                      {sb.bonuses.map((b) => (
                        <span key={b.value} className="bonus-ribbon">
                          {bonusLabel(b)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="holeend-actions">
        <button className="icon-btn he-sc" onClick={onScores} aria-label="Scorecard">
          <IconScores />
        </button>
        {gameOver ? (
          <button className="btn btn-gold btn-big he-next" onClick={onResults} data-autofocus>
            <AmphoraIcon size={22} /> Results
          </button>
        ) : mode === 'online' && myReady ? (
          <div className="ready-wait">
            <span className="spinner small" aria-hidden="true" /> Waiting…
            {isHost && onForce && othersPending && (
              <button className="btn btn-sm btn-primary" onClick={onForce}>
                Start now
              </button>
            )}
          </div>
        ) : me !== null ? (
          <button className="btn btn-gold btn-big he-next" onClick={onNext} data-autofocus>
            <WorldMedallion world={nextWorld} size={26} /> Next hole
          </button>
        ) : (
          <span className="ready-wait">
            <span className="spinner small" aria-hidden="true" /> Waiting…
          </span>
        )}
      </div>
    </div>
  );
}
